import React from 'react';
import { Pressable, View } from 'react-native';
import { Link } from 'expo-router';
import { Image } from 'expo-image';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { MODEL_TOTAL } from '@/constants/modelInventory.generated';
import { MODEL_BRANDS, MODEL_CDN } from './landingModels';
import { GhostCta, PrimaryCta, Section, useInteractive, useLanding } from './LandingKit';
import { fluid, isWeb } from './landingTokens';

const CHAT_BRAND_NAMES = ['GPT', 'Claude', 'Gemini', 'DeepSeek', 'Qwen', 'Kimi', 'GLM', 'Arya'];
const CHAT_BRANDS = MODEL_BRANDS.filter(brand => CHAT_BRAND_NAMES.includes(brand.name));

const CHAT_FEATURES = [
  { title: 'A conversation for every day.', copy: 'Ask a quick question, brainstorm an idea, polish your writing, or work through code. Keep exploring with follow-up questions.' },
  { title: 'You choose who answers.', copy: 'Pick a favorite, switch models between messages, or let Auto choose a model suited to your request.' },
  { title: 'Bring more to the conversation.', copy: 'Search the web, attach files, and ask questions about your documents. Keep your research and conversations in one workspace.' },
];

const FILES = [
  { title: 'Documents', example: 'A research brief, business plan, or proposal.', formats: 'Word / PDF' },
  { title: 'Presentations', example: 'A pitch deck, lesson, or report for the room.', formats: 'PowerPoint' },
  { title: 'Spreadsheets', example: 'A budget, scorecard, or analysis with formulas.', formats: 'Excel / CSV' },
  { title: 'Code & games', example: 'A website, a useful script, or a playable game.', formats: 'HTML / source code' },
  { title: 'Video & audio', example: 'A finished film with music and voiceover.', formats: 'MP4 / audio' },
];

const WORKSPACE = [
  { title: 'Voice conversations', copy: 'Talk through an idea without typing it all out. Ask questions, plan your day, or rehearse a pitch, with a transcript to come back to.', href: '/voice' },
  { title: 'Scheduled tasks', copy: 'Set your morning brief, weekly report, or recurring research to run automatically. Results arrive in your chat without having to ask each time.', href: '/tasks/browse' },
  { title: 'Your existing history', copy: 'Bring your conversations from ChatGPT, Claude, Grok, or another Gab AI account. Find past work without starting over.', href: '/docs/import-chats' },
  { title: 'Memory & privacy', copy: 'Spend less time explaining the same context. Choose what carries into future conversations, and edit or delete your memories whenever you want.', href: '/privacy-policy' },
];

const TOOLS = [
  { label: 'Image generator', href: '/tools/image-generator' },
  { label: 'Image editor', href: '/tools/image-editor' },
  { label: 'Blog writer', href: '/tools/blog-writer' },
  { label: 'Research assistant', href: '/tools/research-assistant' },
  { label: 'Code generator', href: '/tools/code-generator' },
  { label: 'Presentation maker', href: '/tools/powerpoint-generator' },
];

/** An ordinary text link, with the same anchor semantics on every breakpoint. */
export function EditorialLink({ href, children, style }) {
  const { c } = useLanding();
  const { hovered, handlers } = useInteractive();
  return (
    <Link href={href} asChild>
      <Pressable {...handlers} accessibilityRole="link" style={{ minHeight: 40, flexDirection: 'row', alignItems: 'center', gap: 10, ...(isWeb ? { cursor: 'pointer' } : {}), ...style }}>
        <Text style={{ flexShrink: 1, color: hovered ? c.accent : c.inkSoft, fontSize: 14, lineHeight: 21, ...(isWeb ? { textDecorationLine: hovered ? 'underline' : 'none' } : {}) }}>{children}</Text>
        <Icon name="arrow-up-right" size={10} color={c.muted} />
      </Pressable>
    </Link>
  );
}

function StorySection({ children, label, background }) {
  const { c, width } = useLanding();
  return (
    <Section label={label} background={background} style={{ borderTopWidth: 1, borderTopColor: c.rule }} contentStyle={{ paddingTop: fluid(width, 48, 76), paddingBottom: fluid(width, 48, 76) }}>
      {children}
    </Section>
  );
}

function SectionTitle({ children, style }) {
  const { c, width } = useLanding();
  const size = fluid(width, 30, 40);
  return <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: size, lineHeight: Math.round(size * 1.12), letterSpacing: -size * 0.035, ...(isWeb ? { whiteSpace: 'pre-line' } : {}), ...style }}>{children}</Text>;
}

export function LandingChatSection() {
  const { c, isXS, isCompact, width } = useLanding();
  return (
    <StorySection label="Chat mode">
      <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 36 : 64 }}>
        <View style={{ flex: isCompact ? undefined : 1, minWidth: 0 }}>
          <Text weight="medium" style={{ color: c.accent, fontSize: 15, marginBottom: 18 }}>Chat mode</Text>
          <SectionTitle>{'Your favorite AI.\nAll in one place.'}</SectionTitle>
          <Text style={{ color: c.inkSoft, fontSize: 16, lineHeight: 26, marginTop: 22, maxWidth: 420 }}>Chat with leading models from frontier labs and the open-source community. GPT, Claude, Gemini, DeepSeek, and more, together in one familiar place.</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', columnGap: 24, rowGap: 8, marginTop: 26 }}>
            <PrimaryCta restrained label="Start chatting" href="/app" full={isXS} />
            <GhostCta label="Browse all models" href="/models" />
          </View>
        </View>
        <View style={{ flex: isCompact ? undefined : 1.25, minWidth: 0, justifyContent: 'center' }}>
          <Text weight="semibold" style={{ color: c.ink, fontSize: 16, lineHeight: 24, marginBottom: 12 }}>Frontier labs. Open-source favorites. Your choice.</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {CHAT_BRANDS.map(brand => (
              <View key={brand.name} style={{ width: isXS ? '50%' : '25%', minHeight: 76, flexDirection: 'row', alignItems: 'center', gap: 9, borderBottomWidth: 1, borderBottomColor: c.rule, paddingRight: 8 }}>
                <Image source={{ uri: `${MODEL_CDN}${brand.logo}` }} style={{ width: 24, height: 24, borderRadius: 4 }} contentFit="contain" accessibilityLabel={`${brand.name} logo`} />
                <Text weight="medium" style={{ color: c.ink, fontSize: 15, letterSpacing: -0.25 }}>{brand.name}</Text>
              </View>
            ))}
          </View>
          <Text style={{ color: c.inkSoft, fontSize: 14, lineHeight: 23, marginTop: 22 }}><Text weight="semibold" style={{ color: c.ink, fontSize: 14 }}>{MODEL_TOTAL} models. One account.</Text> Explore the full library for chat, images, video, audio, and more.</Text>
        </View>
      </View>
      <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 24 : 36, marginTop: fluid(width, 36, 48), paddingTop: fluid(width, 28, 36), borderTopWidth: 1, borderTopColor: c.rule }}>
        {CHAT_FEATURES.map(feature => (
          <View key={feature.title} style={{ flex: isCompact ? undefined : 1, minWidth: 0 }}>
            <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 18, lineHeight: 25, letterSpacing: -0.25 }}>{feature.title}</Text>
            <Text style={{ color: c.inkSoft, fontSize: 15, lineHeight: 24, marginTop: 10 }}>{feature.copy}</Text>
          </View>
        ))}
      </View>
    </StorySection>
  );
}

function LandingValueStory() {
  const { c, band, isCompact, width } = useLanding();
  return (
    <View style={{ width: '100%' }}>
      <StorySection label="Files you can create" background={band.bg}>
        <View style={{ flexDirection: isCompact ? 'column' : 'row', justifyContent: 'space-between', gap: 24, marginBottom: fluid(width, 32, 42) }}>
          <SectionTitle style={{ maxWidth: 460 }}>Real files. Less busywork.</SectionTitle>
          <View style={{ maxWidth: 430 }}>
            <Text style={{ color: c.inkSoft, fontSize: 16, lineHeight: 26 }}>Gab AI Work Agent delivers files you can use, edit, and share. Review documents, slides, and spreadsheets beside the chat, so you spend less time copying answers into another app.</Text>
            <EditorialLink href="/docs/editors" style={{ marginTop: 10 }}>Explore the editors</EditorialLink>
          </View>
        </View>
        <View style={{ borderTopWidth: 1, borderTopColor: c.ruleStrong }}>
          {FILES.map(file => (
            <View key={file.title} style={{ flexDirection: isCompact ? 'column' : 'row', alignItems: isCompact ? 'flex-start' : 'baseline', gap: isCompact ? 8 : 32, paddingVertical: isCompact ? 23 : 26, borderBottomWidth: 1, borderBottomColor: c.ruleStrong }}>
              <Text tag="h3" weight="medium" style={{ flex: isCompact ? undefined : 0.9, color: c.ink, fontSize: 20, lineHeight: 27, letterSpacing: -0.4 }}>{file.title}</Text>
              <Text style={{ flex: isCompact ? undefined : 1.7, color: c.inkSoft, fontSize: 15, lineHeight: 24 }}>{file.example}</Text>
              <Text style={{ flex: isCompact ? undefined : 0.8, color: c.muted, fontSize: 12, lineHeight: 20, textAlign: isCompact ? 'left' : 'right' }}>{file.formats}</Text>
            </View>
          ))}
        </View>
        <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 16 : 48, marginTop: 32 }}>
          <Text style={{ color: c.ink, fontSize: 14, paddingTop: isCompact ? 0 : 10 }}>Or start with a tool</Text>
          <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', columnGap: 24, rowGap: 2 }}>
            {TOOLS.map(tool => <EditorialLink key={tool.href} href={tool.href}>{tool.label}</EditorialLink>)}
          </View>
        </View>
      </StorySection>

      <StorySection label="Your everyday workspace">
        <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 30 : 76 }}>
          <View style={{ flex: isCompact ? undefined : 0.9, minWidth: 0 }}>
            <SectionTitle>Take the routine off your hands.</SectionTitle>
            <Text style={{ color: c.inkSoft, fontSize: 16, lineHeight: 26, marginTop: 22, maxWidth: 360 }}>Schedule recurring tasks, talk through an idea, and pick up where you left off. Keep things moving with less effort and fewer interruptions to your day.</Text>
            <GhostCta label="Get the apps" href="/download" style={{ marginTop: 20 }} />
          </View>
          <View style={{ flex: isCompact ? undefined : 1.55, minWidth: 0 }}>
            {WORKSPACE.map((item, index) => (
              <View key={item.title} style={{ paddingTop: index === 0 ? 0 : 24, paddingBottom: 24, borderBottomWidth: 1, borderBottomColor: c.rule }}>
                <EditorialLink href={item.href}><Text weight="semibold" style={{ color: c.ink, fontSize: 18 }}>{item.title}</Text></EditorialLink>
                <Text style={{ color: c.inkSoft, fontSize: 15, lineHeight: 24, marginTop: 5, maxWidth: 560 }}>{item.copy}</Text>
              </View>
            ))}
          </View>
        </View>
      </StorySection>
    </View>
  );
}
export default React.memo(LandingValueStory);
